import { Quote } from "lucide-react";
import React, { useEffect, useState } from "react";

type QuoteSelection = {
  text: string;
  left: number;
  top: number;
};

export const quoteForReply = (text: string) =>
  `${text
    .trim()
    .split(/\r?\n/)
    .map(line => `> ${line}`)
    .join("\n")}\n\n`;

export const appendQuote = (draft: string, selectedText: string) => {
  const quote = quoteForReply(selectedText);
  return draft.trimEnd() === "" ? quote : `${draft.trimEnd()}\n\n${quote}`;
};

/** Offers to move selected transcript text into the reply composer as a quote. */
export const ChatSelectionQuote = ({
  containerRef,
  onQuote,
  label,
}: {
  containerRef: React.RefObject<HTMLElement>;
  onQuote: (text: string) => void;
  label: string;
}) => {
  const [selection, setSelection] = useState<QuoteSelection | null>(null);

  useEffect(() => {
    let frame: number | null = null;

    const update = () => {
      const selected = window.getSelection();
      const container = containerRef.current;
      if (
        !selected ||
        selected.isCollapsed ||
        !container ||
        !selected.anchorNode ||
        !selected.focusNode ||
        !container.contains(selected.anchorNode) ||
        !container.contains(selected.focusNode)
      ) {
        setSelection(null);
        return;
      }

      const text = selected.toString().trim();
      if (!text) {
        setSelection(null);
        return;
      }

      const rect = selected.getRangeAt(0).getBoundingClientRect();
      setSelection({
        text,
        left: Math.max(80, Math.min(window.innerWidth - 80, rect.left + rect.width / 2)),
        top: rect.bottom + 44 < window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - 40),
      });
    };

    const finishSelection = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      // The browser finalizes the range after pointer/key handling completes.
      frame = requestAnimationFrame(update);
    };

    const startSelection = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-chat-quote-action]")) return;
      if (frame !== null) cancelAnimationFrame(frame);
      setSelection(null);
    };

    document.addEventListener("pointerdown", startSelection);
    document.addEventListener("pointerup", finishSelection);
    document.addEventListener("keyup", finishSelection);
    window.addEventListener("resize", update);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      document.removeEventListener("pointerdown", startSelection);
      document.removeEventListener("pointerup", finishSelection);
      document.removeEventListener("keyup", finishSelection);
      window.removeEventListener("resize", update);
    };
  }, [containerRef]);

  if (!selection) return null;

  return (
    <button
      data-chat-quote-action
      type="button"
      onMouseDown={event => event.preventDefault()}
      onClick={() => {
        onQuote(selection.text);
        window.getSelection()?.removeAllRanges();
        setSelection(null);
      }}
      className="fixed z-50 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-outline bg-surface px-3 py-1.5 text-xs font-medium text-text shadow-lg transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      style={{ left: selection.left, top: selection.top }}
    >
      <Quote className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </button>
  );
};
